import { Link } from "react-router-dom";
import {
  ArrowRight,
  Landmark,
  PiggyBank,
  Globe2,
  FileCheck2,
  Briefcase,
  ShieldCheck,
  Compass,
} from "lucide-react";

const TOPICS = [
  {
    icon: PiggyBank,
    title: "TSP Withdrawal Tax Strategy",
    blurb: "Traditional vs. Roth TSP taxation, partial withdrawals, installment payments, single-sum distributions, and the tax mechanics of TSP-to-IRA rollovers.",
  },
  {
    icon: Landmark,
    title: "FERS & CSRS Pension Taxation",
    blurb: "How the federal basic benefit is taxed in retirement, the Simplified Method recovery of your contributions, and the interaction with state-of-residence rules.",
  },
  {
    icon: Compass,
    title: "Social Security with a Federal Pension",
    blurb: "How the Windfall Elimination Provision (WEP) and Government Pension Offset (GPO) interact with CSRS, mixed federal/private careers, and spousal benefits.",
  },
  {
    icon: Briefcase,
    title: "Special Retirement Supplement (SRS)",
    blurb: "Taxation of the FERS SRS for those retiring before age 62, the earnings test, and coordination with eventual Social Security claiming.",
  },
  {
    icon: Globe2,
    title: "Overseas & Foreign Service Tax Planning",
    blurb: "State-of-legal-residence questions, Foreign Earned Income Exclusion eligibility, post differentials, and the tax mechanics of returning stateside.",
  },
  {
    icon: FileCheck2,
    title: "Military Service Credit (Buyback)",
    blurb: "Tax treatment of military service deposits, the deductibility of interest, and how the buyback decision affects FERS/CSRS pension calculations.",
  },
];

const WHO = [
  "Current federal employees evaluating retirement timing",
  "Recently retired federal employees navigating their first 1099-R",
  "FERS, CSRS, and CSRS-Offset annuitants",
  "Foreign Service Officers and overseas-posted personnel",
  "USPS and Postal Service employees",
  "Active-duty and recently separated military preparing for the civilian tax world",
  "Federal contractors transitioning to W-2 federal employment (and back)",
];

export default function FederalEmployeesPage() {
  return (
    <div data-testid="page-federal-employees" className="bg-[#F9F8F6]">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-20 md:pb-28 relative">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="text-xs uppercase tracking-[0.3em] text-[#8E6A2B] mb-6">— For Federal Employees</div>
              <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-[#0F292F] font-light tracking-tight leading-[1.05]">
                Built for federal<br />
                <span className="italic text-[#8E6A2B]">employees</span> & retirees.
              </h1>
              <p className="mt-8 text-lg md:text-xl text-[#52525B] max-w-xl leading-relaxed">
                The federal compensation system is not the same as the private sector — and
                the tax treatment of TSP, FERS, CSRS, the Special Retirement Supplement, and
                Social Security with WEP/GPO offsets has nuances most general practitioners
                miss. H. Elliot Associates brings specialized knowledge of these benefits to
                every retirement and tax conversation.
              </p>
              <p className="mt-6 text-base text-[#52525B] italic max-w-xl leading-relaxed">
                Through every season of public service — steady or uncertain — careful
                planning is what keeps a federal career on course.
              </p>
              <div className="mt-12 flex flex-wrap gap-5">
                <Link to="/book" data-testid="fed-book-btn" className="btn-primary">
                  Book a Consultation <ArrowRight size={16} />
                </Link>
                <Link to="/contact" data-testid="fed-contact-btn" className="btn-secondary">
                  Ask a Question
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="bg-[#0F292F] text-white p-8 md:p-10">
                <div className="text-xs uppercase tracking-[0.25em] text-[#CFA968] mb-5">— Who We Serve</div>
                <ul className="space-y-3 text-sm">
                  {WHO.map((w) => (
                    <li key={w} className="flex items-start gap-3">
                      <span className="w-5 h-px bg-[#CFA968] mt-3 flex-shrink-0" />
                      <span className="text-white/85 leading-relaxed">{w}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="hairline max-w-7xl mx-auto" />

      {/* DIFFERENTIATOR */}
      <section className="bg-[#F3F1EC] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.3em] text-[#8E6A2B] mb-6">Why this niche</div>
            <h2 className="font-serif text-4xl md:text-5xl text-[#0F292F] font-light leading-tight">
              The questions that confuse most CPAs.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-[#1A1A1A]">
            <p className="text-lg leading-relaxed">
              <em>"Should I keep my Traditional TSP, or roll it to an IRA at retirement?"</em>{" "}
              <em>"How is my Special Retirement Supplement taxed if I take a part-time
              consulting gig?"</em> <em>"Will WEP reduce my Social Security?"</em>{" "}
              <em>"I'm moving from DC to Florida — when does the residency change actually
              count for state tax?"</em>
            </p>
            <p className="text-lg leading-relaxed">
              These questions are the daily reality of every federal employee approaching
              retirement, and the wrong answer can cost five figures over a thirty-year
              retirement. They require specialized expertise in the federal compensation
              system, applied carefully to each client's specific situation.
            </p>
          </div>
        </div>
      </section>

      {/* TOPICS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="text-xs uppercase tracking-[0.3em] text-[#8E6A2B] mb-5">Focus Areas</div>
        <h2 className="font-serif text-4xl md:text-5xl text-[#0F292F] font-light tracking-tight max-w-3xl leading-tight mb-16">
          Six topics we know cold.
        </h2>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-black/10 border border-black/10">
          {TOPICS.map((t) => (
            <article
              key={t.title}
              data-testid={`fed-topic-${t.title.toLowerCase().replace(/\s+/g, "-").replace(/[&()/]/g, "")}`}
              className="bg-white p-8 md:p-10 hover:bg-[#F3F1EC] transition-colors duration-500"
            >
              <t.icon size={28} strokeWidth={1.2} className="text-[#8E6A2B] mb-6" />
              <h3 className="font-serif text-2xl text-[#0F292F] mb-4 leading-tight">{t.title}</h3>
              <p className="text-[#52525B] text-sm leading-relaxed">{t.blurb}</p>
            </article>
          ))}
        </div>

        <p className="mt-10 text-sm text-[#52525B] max-w-3xl leading-relaxed">
          Engagements focus on planning, taxation, and the tax mechanics of federal-employee
          benefits and decisions. They do not constitute investment advice or the recommendation,
          selection, or management of securities.
        </p>
      </section>

      {/* TRUST CALLOUT */}
      <section className="bg-[#0F292F] text-white">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-20 md:py-28 text-center">
          <ShieldCheck size={32} strokeWidth={1.2} className="text-[#CFA968] mx-auto mb-8" />
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-light leading-tight italic">
            "There's a real difference between reading about the federal compensation
            system and having spent years inside it. That's the difference we bring."
          </h2>
          <div className="mt-10 text-sm tracking-wide text-white/60">
            <span className="font-semibold text-white">Harold Hodges</span> · Founder
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-black/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <h2 className="font-serif text-4xl md:text-5xl text-[#0F292F] font-light tracking-tight">
              Bring your federal benefits questions.
            </h2>
            <p className="mt-4 text-lg text-[#52525B] max-w-2xl">
              A 30-minute introductory consultation, at no cost. Bring your most recent FERS
              estimate, TSP statement, or a question you've been holding onto — we'll talk
              through it.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link to="/book" data-testid="fed-cta-book" className="btn-primary">
              Book a Consultation <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
